import { animate, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment';
import {
  Observable,
  combineLatest,
  firstValueFrom,
  forkJoin,
  from,
  fromEvent,
  merge,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  pairwise,
  retry,
  startWith,
  switchMap,
  take,
  timeout,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppService } from './app.service';
import { CurrencyWalletService } from './layouts/currency-wallet/currency-wallet.service';
import { ActivityService } from './pages/activity/activity.service';
import { CAMPAIGN_REDIRECT_FALLBACK_STORAGE_KEY, LoginService } from './pages/login-dialog/login.service';
import { MiniGameService } from './pages/minigame/minigame.service';
import { SettingsService } from './pages/settings/settings.service';
import { UserAssetsService } from './pages/user-asset/user-assets.service';
import { WalletDialogType, WalletService } from './pages/wallet/wallet.service';
import { AccountApi } from './shared/apis/account.api';
import { AuthApi } from './shared/apis/auth.api';
import { CountryApi } from './shared/apis/country.api';
import { DepositApi } from './shared/apis/deposit.api';
import { GameApi } from './shared/apis/game.api';
import { KycApi } from './shared/apis/kyc-basic.api';
import { ResourceApi } from './shared/apis/resource.api';
import { SettingsApi } from './shared/apis/settings.api';
import { BannerPopupService } from './shared/components/banner-popup/banner-popup.service';
import { CustomerServiceService } from './shared/components/customer-service/customer-service.service';
import { EddPopupService } from './shared/components/edd-popup/edd-popup.service';
import { ThirdAuthM8Service } from './shared/components/third-auth-m8/third-auth-m8.service';
import { AccountInforData } from './shared/interfaces/account.interface';
import { CurrenciesInterface } from './shared/interfaces/deposit.interface';
import { LabelRoutesInfo, ProviderRoutesInfo } from './shared/interfaces/game.interface';
import { TenantConfig } from './shared/interfaces/resource.interface';
import { defaultAvatarPc } from './shared/interfaces/settings.interface';
import { CurrencyBalance } from './shared/interfaces/wallet.interface';
import { CacheService } from './shared/service/cache.service';
import { ChatService } from './shared/service/chat.service';
import { DataCollectionService } from './shared/service/data-collection.service';
import { EncryptService } from './shared/service/encrypt.service';
import { GeneralService } from './shared/service/general.service';
import { GlobalSizeChange, LayoutService } from './shared/service/layout.service';
import { LocaleService } from './shared/service/locale.service';
import { LOGIN_TOKEN_KEY, LocalStorageService } from './shared/service/localstorage.service';
import { NativeAppService } from './shared/service/native-app.service';
import { SentryService } from './shared/service/sentry.service';
import { RouteSeoData, SeoRouteContext, SeoService } from './shared/service/seo.service';
import { SWService } from './shared/service/service-worker.service';
import { SignalrService } from './shared/service/signalr.service';
import { TelegramService } from './shared/service/telegram.service';
import { ToastService } from './shared/service/toast.service';
import { Web3WalletService } from './shared/service/web3-wallets/web3-wallet.service';
import { resolveDepositedDefaultCurrency } from './shared/utils/deposit-default-currency';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('fade', [
      transition(':enter', [style({ opacity: 0 }), animate('0.2s ease-in-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('0.2s ease-in-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private http: HttpClient,
    public appService: AppService,
    private layoutService: LayoutService, // 请勿删除，它需要初始化
    private miniGameService: MiniGameService,
    private activityService: ActivityService,
    private depositApi: DepositApi,
    private authApi: AuthApi,
    private localeService: LocaleService,
    private resourceApi: ResourceApi,
    private localStorageService: LocalStorageService,
    private toast: ToastService,
    private encryptService: EncryptService,
    private accountApi: AccountApi,
    private countryApi: CountryApi,
    private elementRef: ElementRef,
    private generalService: GeneralService,
    private cacheService: CacheService,
    private signalrService: SignalrService,
    private dataCollectionService: DataCollectionService,
    private route: ActivatedRoute,
    private router: Router,
    private nativeAppService: NativeAppService,
    private userAssetsService: UserAssetsService,
    private currencyWalletService: CurrencyWalletService,
    private eddPopup: EddPopupService,
    private sentryService: SentryService,
    private swService: SWService,
    private customerServiceService: CustomerServiceService,
    private settingsApi: SettingsApi,
    private settingsService: SettingsService,
    private chatService: ChatService,
    private bannerPopupService: BannerPopupService,
    private walletService: WalletService,
    private wab3WalletService: Web3WalletService,
    public loginService: LoginService,
    private thirdAuthM8Service: ThirdAuthM8Service,
    private telegramService: TelegramService,
    private kycApi: KycApi,
    private seoService: SeoService,
    private gameApi: GameApi,
  ) {
    this.telegramService.setupTgBridgeListener();
  }

  /** 是否为线上版本 */
  isOnline: boolean = true;

  /** 版本号 */
  version: string = '';

  /** 后端版本号 */
  backendVersion: string = '';

  /** 是否准备好 */
  isReady: boolean = false;

  /**根据IP地址获取当前货币 */
  countryCurrency!: string;

  /** 获取默认头像的url列表 */
  avatarList!: defaultAvatarPc[];

  /** 未登入訪問使用的 IP 地址 */
  private ipAddress: string | null = null;

  /** 是否已推送首頁訪問事件 */
  private visitEventPushed = false;

  /**
   * 检测storage变化
   * @param event
   */
  @HostListener('window:storage', ['$event'])
  storageChange(event: StorageEvent) {
    const tokenKey = this.localStorageService.getStorageFullKey(LOGIN_TOKEN_KEY);
    // 僅處理登入 token 變更事件
    if (event.key !== tokenKey) return;
    // 僅在「新增或刪除 token」時觸發（登入／登出）
    const addedOrRemoved =
      (event.oldValue === null && event.newValue !== null) || (event.oldValue !== null && event.newValue === null);
    if (!addedOrRemoved) return;
    // 若在 App WebView 環境（App 端預先注入 window.__IN_APP__ = true）
    const inApp = (window as any).__IN_APP__ === true;
    if (inApp) return; // 不重整，交給 App 處理
    // 一般瀏覽器：刷新頁面以同步登入狀態
    this.appService.reFresh();
  }

  async ngOnInit() {
    // 是否為 Telegram Mini App
    const appParams = new URLSearchParams(location.search);
    sessionStorage.setItem('isTgApp', appParams.get('isTgApp') === 'true' ? 'true' : 'false');

    //是否线上环境
    this.isOnline = environment.isOnline;

    //版本号
    window.version = this.version = environment.version;

    // 创建manifest
    this.createManifest();

    // 后端版本号
    this.onBackendVersion();

    //设置Moment
    window.moment = moment;

    // 是不是原生app 访问
    this.onCheckNativeApp();

    //初始化语言
    if (!this.languageCodeInit()) return;

    // 初始化CacheStorage缓存对象
    if ('caches' in window && 'serviceWorker' in navigator) {
      this.appService.curCaches = await window.caches.open(this.appService.cacheName);
    }

    //初始化商户信息、ApiUrl
    if (!(await this.tenantInit())) return;

    //初始化埋点
    this.dataCollectionService.init();
    this.dataCollectionService.setEnterTime('allpage');
    const isApp = this.dataCollectionService.isMobileApp();
    if (isApp) {
      window.location.href = `https://app7.casppxbo.com/?lang=${this.appService.languageCode}`;
    }
    //初始化客服
    this.customerServiceService.init(this.appService.languageCode);
    //初始化语言文件、Token等
    await this.basicInit();
    this.initDialogQueryWatcher();
    this.initRouteSeoWatcher();
    this.initCampaignRedirectFallbackWatcher();

    // 記錄未登入首頁訪問
    this.initVisitEventTracking();

    //订阅全局内容尺寸变更，给 app-root 增减相应 padding
    this.appService.globalSizeChange$.pipe(untilDestroyed(this)).subscribe(v => v && this.changePadding(v));

    //退出登录
    this.appService.logoutSubject$.pipe(untilDestroyed(this)).subscribe(async () => {
      this.customerServiceService.resetZendeskToGuest();
      await firstValueFrom(this.authApi.logout());

      // 退出登录后清除钱包连接
      await this.wab3WalletService.disconnectWallet();

      // 清除通知类型缓存
      try {
        localStorage.removeItem('noticeTypeList');
      } catch {
        // ignore storage errors
      }

      this.appService.softlyLogout();

      // Close the Mini App if in Telegram mini app
      const isTgApp = sessionStorage.getItem('isTgApp') === 'true';
      if (isTgApp) {
        window.Telegram.WebApp.close();
      }
    });

    //阻止Sfari双击
    this.generalService.blockSfariDoubleClick();

    //加载moment多语言
    if (this.appService.languageCode != 'en-us') {
      this.loadExternalScript(
        `assets/scripts/moment/locale/${this.appService.languageCode}.js`,
        `moment-${this.appService.languageCode}`,
      );
    }

    combineLatest([this.appService.ipInfoReady$, this.appService.isCountriesReady$])
      .pipe(
        untilDestroyed(this),
        mergeMap(([ipReady, isCountriesReady]) => {
          if (ipReady && isCountriesReady) {
            // 未登录
            this.appService.currentCountry$.next(
              this.cacheService.countries.find(x => x.iso === this.appService.countryCode),
            );
          }
          return this.appService.userInfo$.pipe(
            filter(v => !!v),
            take(1),
          );
        }),
      )
      .subscribe(userInfo => {
        if (userInfo?.mobileRegionCode) {
          //  已经绑定过手机 用户 且登录
          this.appService.currentCountry$.next(
            this.cacheService.countries.find(x => x.iso === userInfo?.mobileRegionCode),
          );
        }
      });

    // 检查是否是新注册登录
    if (this.localStorageService.isNewRegisterLogin) {
      this.walletService.openWallet();
      this.localStorageService.isNewRegisterLogin = false;
    }

    // Init telegram mini app
    if (window.Telegram?.WebApp?.initData) {
      sessionStorage.setItem('isTgApp', 'true');

      // Expand the Mini App to the maximum height
      window.Telegram.WebApp.expand();

      // Disable vertical swipes
      window.Telegram.WebApp.disableVerticalSwipes();

      // 沒有 token 自動登入
      if (!this.localStorageService.loginToken) {
        this.autoLoginTelegramMiniApp();
      } else {
        window.Telegram.WebApp.ready();
      }
    }

    window.Telegram?.WebApp?.onEvent('activated', () => {
      const isAndroid = /Android/i.test(navigator.userAgent);

      // 當使用者在 Android 裝置從最小化開啟 Telegram 應用程式時，強制登出，避免帳號登入有問題
      if (isAndroid && window.Telegram.WebApp.isActive === true) {
        this.appService.logoutSubject$.next(true);
        window.Telegram.WebApp.close();
      }
    });
  }

  private async initForceKycFlag() {
    //測試環境使用變數
    // if (this.appService.tenantConfig$.value?.config?.kycEnabled) {
    //   this.localStorageService.forceKycFlag = this.appService.tenantConfig$.value?.config?.kycEnabled === 'true';
    //   return;
    // }

    try {
      const response = await firstValueFrom(this.kycApi.getIsForceKyc());
      this.localStorageService.forceKycFlag = response?.data?.isForceKyc ?? false;
    } catch (error) {
      console.error(error);
      this.localStorageService.forceKycFlag = false;
    }
  }

  private initVisitEventTracking() {
    const navigation$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(new NavigationEnd(0, this.router.url, this.router.url)),
      map(event => event.urlAfterRedirects || event.url),
    );

    combineLatest([navigation$, this.appService.ipInfoReady$])
      .pipe(
        untilDestroyed(this),
        filter(([, ipReady]) => ipReady),
        filter(() => !this.visitEventPushed),
        filter(([url]) => this.isLandingPage(url)),
        switchMap(() => from(this.buildVisitEventPayload())),
        take(1),
      )
      .subscribe(payload => {
        this.visitEventPushed = true;
        this.dataCollectionService.gtmPush('visit', payload);
      });
  }

  private isLandingPage(url: string): boolean {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);
    return segments.length === 1;
  }

  // 每次導航結束時，如果不是 404，就清掉 flag
  private initCampaignRedirectFallbackWatcher() {
    this.router.events
      .pipe(
        // 只在導航完成後處理，同時讓後續 event 被推斷為 NavigationEnd。
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(event => {
        const url = event.urlAfterRedirects || event.url;
        const path = url.split('?')[0];
        const lastSegment = path.split('/').filter(Boolean).pop();

        if (lastSegment !== '404') {
          sessionStorage.removeItem(CAMPAIGN_REDIRECT_FALLBACK_STORAGE_KEY);
        }
      });
  }

  private pushPendingRegistrationEvent(account: AccountInforData | null) {
    const pendingFlag = this.localStorageService.registrationSuccessPending;
    if (!pendingFlag) return;

    const payload = this.dataCollectionService.buildSuccessRegistrationPayload(account);
    if (payload) {
      this.dataCollectionService.gtmPush('success-registration', payload);
    }

    this.localStorageService.registrationSuccessPending = false;
  }

  private showPendingKycToast() {
    const pendingToast = this.localStorageService.kycLitePendingToast;
    if (!pendingToast) return;

    this.toast.show(pendingToast);
    this.localStorageService.kycLitePendingToast = null;
  }

  private async buildVisitEventPayload(): Promise<Record<string, string>> {
    const payload: Record<string, string> = {};
    const city = await this.dataCollectionService.lookupCityByIp(this.ipAddress || undefined);

    payload.city = city || '';

    payload.userid = this.localStorageService.loginToken ? this.appService.userInfo$.value?.uid || '' : '';

    return payload;
  }

  private initRouteSeoWatcher() {
    const navigation$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.routerState.snapshot.root),
      map(snapshot => this.buildRouteSeoPayload(snapshot)),
    );

    combineLatest([this.appService.langFileReady$.pipe(filter(Boolean)), navigation$])
      .pipe(
        untilDestroyed(this),
        map(([, payload]) => payload),
        filter((payload): payload is { stack: RouteSeoData[]; context: SeoRouteContext } => !!payload),
      )
      .subscribe(payload => {
        this.seoService.applyRouteSeo(payload.stack, payload.context);
      });
  }

  private buildRouteSeoPayload(
    root: ActivatedRouteSnapshot | null,
  ): { stack: RouteSeoData[]; context: SeoRouteContext } | null {
    if (!root) return null;

    const stack: RouteSeoData[] = [];
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = root;
    let leaf: ActivatedRouteSnapshot = root;

    while (current) {
      const seo = current.data?.['seo'] as RouteSeoData | undefined;
      if (seo) {
        stack.push(seo);
      }

      if (current.url?.length) {
        const fragment = current.url
          .map(part => part.path)
          .filter(Boolean)
          .join('/');
        if (fragment) {
          segments.push(fragment);
        }
      }

      if (current.firstChild) {
        current = current.firstChild;
        leaf = current;
      } else {
        break;
      }
    }

    const urlPath = '/' + segments.join('/');
    const normalizedUrl = urlPath.replace(/\/{2,}/g, '/');
    const context: SeoRouteContext = {
      params: leaf.params ?? {},
      queryParams: leaf.queryParams ?? {},
      url: normalizedUrl.length > 1 ? normalizedUrl : '/',
      route: leaf,
    };

    return { stack, context };
  }

  private initDialogQueryWatcher() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        untilDestroyed(this),
      )
      .subscribe(async event => {
        const urlTree = this.router.parseUrl(event.urlAfterRedirects || event.url);
        const dialog = urlTree.queryParams?.dialog?.toLowerCase();

        switch (dialog) {
          case 'login':
            if (!this.localStorageService.loginToken) this.loginService.openLogin();
            break;
          case 'register':
            if (!this.localStorageService.loginToken) this.loginService.openRegister();
            break;
          case 'reset-password':
            if (!this.localStorageService.loginToken) this.loginService.openResetPassword();
            break;
          case 'deposit':
          case 'wallet': // 支援 wallet 舊寫法
            if (this.localStorageService.loginToken) {
              this.walletService.openWallet();
            } else {
              this.localStorageService.pendingLoginRedirectUrl = event.urlAfterRedirects || event.url;
              this.appService.jumpToLogin();
            }
            break;
          case 'deposit-result':
            if (this.localStorageService.loginToken) {
              const crypto = urlTree.queryParams?.crypto;
              const cryptoAmount = urlTree.queryParams?.cryptoAmount;
              const orderId = urlTree.queryParams?.orderId as string;
              const status = urlTree.queryParams?.status;
              const type = 'buy-crypto';

              const res = await firstValueFrom(this.depositApi.getDepositOrderStateInfo(orderId));
              const { state }: { state: string } = res?.data || {};
              const { title, content, orderStatus } = {
                Success: {
                  title: 'Crypto purchase successful',
                  content: 'Your crypto has been credited to your account',
                  orderStatus: 'success',
                },
                Fail: {
                  title: 'Crypto purchase was unsuccessful',
                  content: 'We were unable to process your crypto purchase',
                  orderStatus: 'fail',
                },
              }[state] ?? {
                title: 'Crypto purchase has been initiated',
                content: 'You can track its progress in the transactions page',
                orderStatus: 'pending',
              };
              this.walletService.openWallet();

              this.walletService.walletDetailsData = {
                header: 'Buy Crypto',
                status: state === 'Fail' ? 'fail' : 'success',
                title,
                content,
                typeTitle: 'Deposit',
                data: {
                  hashMsg: '',
                  amount: cryptoAmount,
                  currency: crypto,
                  cryptoOrderId: orderId,
                  status,
                },
                orderStatus: orderStatus as 'success' | 'fail' | 'pending',
              };
              this.walletService.setWalletDialogType(WalletDialogType.DEPOSIT_TRANSACTION_DETAILS);

              if (state === 'Success') {
                const hasDepositBeforeInitiate = this.walletService.getDepositStatusBeforeInitiate();
                const hasDeposit = await this.walletService.ensureDepositStatus();
                const isFirstDeposit = hasDeposit && hasDepositBeforeInitiate === false;
                const eventName = isFirstDeposit ? 'ftd' : hasDeposit ? 'success-deposit' : 'ftd';
                this.walletService.recordDepositStatusBeforeInitiate(hasDeposit);
                this.dataCollectionService.gtmPush(eventName, {
                  type,
                  currency: crypto || '',
                  amount: cryptoAmount || '',
                  transactionid: orderId || '',
                });
              }
            } else {
              this.appService.jumpToLogin();
            }
            break;
          default:
            break;
        }
      });
  }

  /** 获取后端版本号  */
  onBackendVersion() {
    if (this.isOnline) return;
    this.accountApi.getBackendVersion().subscribe(data => {
      this.backendVersion = data;
      // 更新 meta tag
      const metaTag = document.querySelector('meta[name="backend-version"]');
      if (metaTag) {
        metaTag.setAttribute('content', data);
      }
    });
  }

  // 执行未完事情
  onExecuteEventBank() {
    while (window.eventBank.length > 0) {
      const e = window.eventBank.shift();
      switch (e.key) {
        case 'showForbidTip':
          this.appService.langFileReady$
            .pipe(first(v => !!v))
            .subscribe(() => this.appService.showForbidTip(e.payload));
          break;
        case 'eddCheck':
          this.eddPopup.onStart();
          break;

        default:
          break;
      }
    }
  }

  /**
   * 基础初始化
   * @returns //
   */
  private async basicInit() {
    //准备多语言翻译文件
    if (!(await this.getLocale())) {
      // 翻译文件无缓存且请求失败，中断后续所有动作
      return;
    }

    this.setGlobalWaitingText();

    //初始化BasicToken
    if (!this.localStorageService.token) {
      const tokenInit = await this.basicTokenInit();
      if (!tokenInit) return; //基础token请求失败，中断后续所有动作
    }
    // 初始化KYC
    await this.initForceKycFlag();

    if (this.localStorageService.loginToken) {
      await this.getUserInfo();
    }

    combineLatest([
      this.appService.currencies$.pipe(first(v => v.length > 0)),
      this.appService.ipInfoReady$.pipe(
        filter(v => v),
        first(),
      ),
    ])
      .pipe(
        untilDestroyed(this),
        switchMap(([currencies]) => from(this.applyInitialWalletCurrency(currencies))),
      )
      .subscribe();

    //获取IP信息
    this.getIpInfo();

    if (this.localStorageService.token) {
      this.getCountries();
      this.getLang();
      this.getCurrencies();
      timer(0, 1000 * 10 * 60)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.userAssetsService.getAllRate();
        });
    }

    if (this.localStorageService.loginToken) {
      this.getSysConfig();
    } else {
      this.customerServiceService.setVariables();
    }

    const { homeScenesSub, labelRoutes, providerRoutes } = await firstValueFrom(
      forkJoin({
        homeScenesSub: this.miniGameService.getNewSceneData(),
        // 初始化 Route 的 Label Url
        labelRoutes: this.gameApi.getLabelRoutes().pipe(
          map(res => (res.success ? res.data : <LabelRoutesInfo[]>[])),
          catchError(() => of(<LabelRoutesInfo[]>[])),
        ),
        // 初始化 Route 的 Provider Url
        providerRoutes: this.gameApi.getProviderRoutes().pipe(
          map(res => (res.success ? res.data : <ProviderRoutesInfo[]>[])),
          catchError(() => of(<ProviderRoutesInfo[]>[])),
        ),
      }),
    );
    this.miniGameService.homeScenesSub$.next(homeScenesSub);
    this.appService.gameLabelUrlRoutes$.next(labelRoutes);
    this.appService.gameProviderUrlRoutes$.next(providerRoutes);

    this.isReady = true;
    (window as any).globalLoader.hide();

    this.closeGlobalWaiting();

    this.afterReady();
  }

  /**
   * 初始化Language code
   * @returns //
   */
  private languageCodeInit(): boolean {
    if (!window.languageCode) return false;
    this.appService.languageCode = window.languageCode;
    document.body.classList.add(this.appService.languageCode);
    return true;
  }

  /**
   * 获取IP信息
   */
  async getIpInfo() {
    const ipInfo = await firstValueFrom(this.authApi.getIpInfo());
    if (!ipInfo) return;
    this.ipAddress = ipInfo.ip || this.ipAddress;
    this.dataCollectionService.setCityFromIp(ipInfo.city);
    this.dataCollectionService.setIpInfo(ipInfo?.ip);
    this.countryCurrency = ipInfo?.countryCurrency;
    if (ipInfo.countryCode) {
      this.appService.countryCode = ipInfo.countryCode;
    }

    this.appService.ipInfoReady$.next(true);
  }

  //获取语言选择数据
  async getLang() {
    const result = await firstValueFrom(this.countryApi.getalllanguage());
    if (result.length > 0) {
      this.appService.languages$.next(result);
      const curlang = result.find(x => x.code === this.appService.languageCode);
      if (!curlang) {
        window.checkLangSupport(
          this.appService.languageCode,
          result.map(x => x.code),
        );
      }
    }
  }

  /**
   * 加载翻译文件
   */
  async getLocale() {
    const result = await firstValueFrom(this.resourceApi.getLocale(this.appService.languageCode));
    if (result) {
      this.localeService.setLocaleData(result);
      this.appService.langFileReady$.next(true);
    }
    return result;
  }
  // /**
  //  * 加载翻译文件 目前是吃本地文件
  //  */
  // async getLocale() {
  //   const langCode = (this.appService.languageCode || 'en-us').toLowerCase();
  //   const fallbackLang = 'en-us';
  //   const tryLoad = async (code: string) => {
  //     const path = `assets/i18n/${code}.json`;
  //     try {
  //       return await firstValueFrom(this.http.get<any>(path));
  //     } catch (error) {
  //       console.warn(`⚠️ Failed to load language file: ${code}.json`, error);
  //       return null;
  //     }
  //   };
  //   let result = await tryLoad(langCode);

  //   // fallback to en-us if failed
  //   if (!result && langCode !== fallbackLang) {
  //     result = await tryLoad(fallbackLang);
  //   }

  //   if (result) {
  //     this.localeService.setLocaleData(result);
  //     this.appService.langFileReady$.next(true);
  //     //多语言加载完之后动态设置网站title
  //     this.titleService.setTitle(this.localeService.getValue('seo_title'));
  //   }
  //   return result;
  // }

  /**
   * 获取国家数据
   */
  async getCountries() {
    const countries = await firstValueFrom(this.countryApi.getCountries());
    this.cacheService.countries = countries;
    this.appService.isCountriesReady$.next(true);
  }

  //给 app-root 增减相应 padding
  private changePadding(v: GlobalSizeChange) {
    const keys = Object.keys(v) as [];
    keys.forEach(x => {
      const val = v[x] as number;
      const styleKey = {
        top: '--root-padding-top',
        left: '--root-padding-left',
        right: '--root-padding-right',
        bottom: '--root-padding-bottom:',
      }[x];
      this.elementRef.nativeElement.style.setProperty(styleKey, val + 'px');
    });
  }

  /**
   * 获取所有币种并储存
   */
  private async getCurrencies() {
    const data = await firstValueFrom(this.depositApi.getCurrencies());
    if (data) {
      this.appService.currencies$.next(data);

      // 若當前 FIAT 沒有值，則手動尋找可選值（因無法在 CurrencyWalletService constructor 執行，故於此執行）
      const inFiatConvertType = this.currencyWalletService.inFiatConvertType$.getValue();
      if (!inFiatConvertType) {
        const defaultConversionFiat = this.appService.currencies$.value
          .filter(x => x.isVisible && !x.isDigital)
          .find(cur => !cur.isDigital)?.currency;
        this.currencyWalletService.inFiatConvertType$.next(defaultConversionFiat || 'Unknown FIAT');
      }
    }
  }

  /**
   * 幣種列表與 IP 就緒後，決定頂部錢包預設幣（KYC／已存款 crypto／IP 預設）。
   * @param currencies
   */
  private async applyInitialWalletCurrency(currencies: CurrenciesInterface[]): Promise<void> {
    const userInfo = this.appService.userInfo$.value;

    // 登入且有KYC
    if (this.localStorageService.loginToken && !!userInfo?.kycGrade) {
      const code = userInfo.userSetting?.defaultCurrencyType;
      if (code) {
        this.settingsService.setAppServiceCurrentCurrency(code);
      }
      return;
    }

    // 登入但無KYC
    if (this.localStorageService.loginToken) {
      const applied = await this.applyDepositedUserDefaultCurrency(currencies);
      if (applied) return;
    }

    // M8 無法幣：訪客／未存款維持 IP 對應 crypto（setDefaultWalletCurrency 僅 isDigital）
    this.appService.setDefaultWalletCurrency(this.countryCurrency);
  }

  /**
   * 已存款玩家：依 getUserbalance 回傳順序套用存款 crypto 幣種。
   * @param currencies
   */
  private async applyDepositedUserDefaultCurrency(currencies: CurrenciesInterface[]): Promise<boolean> {
    const hasDeposit = await firstValueFrom(this.resourceApi.getUserDeposit().pipe(catchError(() => of(false))));
    if (!hasDeposit) return false;

    const balances = await this.userAssetsService.getUserbalance().catch(() => [] as CurrencyBalance[]);
    const code = resolveDepositedDefaultCurrency(
      balances,
      this.appService.userInfo$.value?.userSetting?.defaultCurrencyType,
      currencies,
    );
    if (!code) return false;

    this.settingsService.setAppServiceCurrentCurrency(code);
    return true;
  }

  /**
   * 初始化BasicToken
   */
  private async basicTokenInit() {
    //获取visitorId
    let visitorId: string;
    if (this.localStorageService.visitorId) {
      visitorId = this.localStorageService.visitorId;
    } else {
      const fp = await FingerprintJS.load();
      const visitorIdObj = await fp.get();
      visitorId = visitorIdObj.visitorId;
      this.localStorageService.visitorId = visitorId;
    }

    const tenant = environment.common.tenant; //商户Id
    const market = '1';
    const content = `nonce=${visitorId}&lang=${this.appService.languageCode}&domain=${
      window.location.host.split(':')[0]
    }&tenant=${tenant}&market=${market}`;
    //Rsa加密
    const iniToken = this.encryptService.encrypt(content);
    const result = await firstValueFrom(this.authApi.getSetUpToken(`${iniToken}`));
    if (result?.data) {
      this.localStorageService.token = result.data.toString();
      return true;
    } else {
      return false;
    }
  }

  /**
   * 获取用户信息 ：登陆后；
   * @returns //
   */
  async getUserInfo() {
    //获取用户信息
    // 當token失敗時，另外判斷是否需自動登入 Telegram Mini App
    const result = await firstValueFrom(this.accountApi.getUserAccountInfo(() => this.autoLoginTelegramMiniApp()));
    if (result?.success) {
      this.pushPendingRegistrationEvent(result.data);
      this.chatService.init();
      this.customerServiceService.setVariables(result?.data);
      this.appService.userInfo$.next(result.data);
      // Execute banner popup first then show eventBank
      this.bannerPopupService.onShowPopupForAfterLogin(result.data.uid, () => this.onExecuteEventBank());

      if (result.data.userSetting) {
        const { defaultCurrencyType, isEnableCredit, invisibilityMode } = result.data.userSetting;
        if (!!result.data.kycGrade && defaultCurrencyType) {
          this.settingsService.setAppServiceCurrentCurrency(defaultCurrencyType);
        }
        this.settingsService.enableCredit$.next(isEnableCredit);
        this.settingsService.invisibleMode$.next(invisibilityMode);
      }

      this.checkForceKycAndRedirect(result.data);

      await this.initHideBuyCrypto(result.data);

      this.showPendingKycToast();
    }

    this.dataCollectionService.init();
  }

  private checkForceKycAndRedirect(userInfo: AccountInforData) {
    if (!this.localStorageService.forceKycRedirectPending) return;

    const needKyc = userInfo.isForceKyc && !userInfo.kycGrade;
    this.localStorageService.forceKycRedirectPending = false;
    if (!needKyc) return;

    const lang =
      this.appService.languageCode || this.localStorageService.languageCode || (window as any).languageCode || 'en';

    void this.router.navigate(['/', lang, 'settings', 'verification'], {
      queryParams: { kycAlert: 'true' },
    });
  }

  private async initHideBuyCrypto(userInfo: AccountInforData) {
    try {
      const hasDeposit = await this.walletService.ensureDepositStatus();
      this.appService.hideBuyCrypto$.next(!hasDeposit && !userInfo.isMA);
    } catch (error) {
      this.appService.hideBuyCrypto$.next(false);
    }
  }

  /**
   * 获取商户各种功能开关
   */
  async getSysConfig() {
    //获取用户信息
    const result = await firstValueFrom(this.accountApi.getSystemConfig());
    if (result?.success) {
      this.appService.sysConfig$.next(result.data);
    }
  }
  /**
   * 动态加载JS
   * @param url url
   * @param id id唯一标识
   * @param callback 成功onload后回调
   * @returns //
   */
  private loadExternalScript(url: string, id: string, callback: () => void = () => {}) {
    //检查是否已存在
    // @ts-ignore
    if (window[`${id}-script-loaded`]) {
      callback();
      return;
    }
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = true;
    script.defer = true;
    script.id = id;
    body.appendChild(script);
    script.onload = () => {
      // @ts-ignore
      window[`${id}-script-loaded`] = true;
      callback();
    };
  }

  /** 创建manifest */
  private createManifest() {
    //检查是否已存在
    // @ts-ignore
    if (window['manifest-script-loaded']) return;
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = `${window.location.origin}/manifest.webmanifest?v=${window.version}`;
    document.head.appendChild(link);
    // @ts-ignore
    window['manifest-script-loaded'] = true;
  }

  /** 删除全局等待的dom */
  closeGlobalWaiting() {
    const el: HTMLDivElement | null = document.querySelector('.global-waiting');
    if (el) el.style.display = 'none';
  }

  /** 设置全局等待界面的多语言 */
  setGlobalWaitingText() {
    const el = document.querySelector('.global-waiting-txt');
    if (el) el.innerHTML = this.localeService.getValue('global_waiting_txt');
  }

  /** 设置网站图标 */
  private setPageImage() {
    //设置网站图标
    const faviconEl: HTMLLinkElement | null = document.querySelector('[rel=icon]');
    const appleTouchIconEl: NodeListOf<HTMLLinkElement> = document.querySelectorAll('[rel=apple-touch-icon]');
    const config = this.appService.tenantConfig$.value?.config;
    if (faviconEl && config?.favicon) faviconEl.href = config.favicon;
    appleTouchIconEl.forEach(x => {
      if (config?.appleTouchIcon) x.href = config.appleTouchIcon;
    });
  }

  /**商户信息初始化、api初始化 */
  private async tenantInit() {
    const result: TenantConfig | null = await firstValueFrom(this.getTenantConfig());

    if (!result) return false;

    // 如果有域名列表，保存到本地
    const domainList = result.config.dl ?? null;
    if (domainList) this.localStorageService.domainList = domainList;

    this.appService.tenantConfig$.next(result);

    // 更新resourceUrl地址
    environment.resourceUrl = result.config.resourceUrl;

    //如果本地没有存日夜版（即首次打开），设置日夜版默认值并存本地。
    if (!this.localStorageService.theme) {
      this.localStorageService.theme = result.colorScheme;
      this.appService.themeSwitch$.next(result.colorScheme);
    }

    //设置头部icon
    this.setPageImage();

    // 节日主题样式
    const themeClass = result.config.themeClass ?? '';
    if (themeClass) document.body.classList.add(themeClass);

    //初始化调用数据
    this.appService.getAvatarList();

    this.signalrService.connectionInit();

    return true;
  }

  /**获取商户配置 */
  getTenantConfig(): Observable<TenantConfig | null> {
    let index = 0;
    const urls = environment.configUrls.split(',').map(x => {
      if (!x.startsWith('http')) x = window.location.origin + x;
      return x.replace('[tenant]', environment.common.tenant);
    });
    return of(true).pipe(
      switchMap(() =>
        this.http.get<TenantConfig>(urls[index] + '?t=' + Date.now(), { headers: { 'ngsw-bypass': 'true' } }),
      ),
      timeout(10 * 1000), //10秒超时
      retry({
        delay: error => {
          this.sentryService.apiError(urls[index], error);
          index = index + 1 >= urls.length ? 0 : index + 1;
          if (index === 0) {
            // 开始测试本地存储的域名并尝试跳转
            this.testDomainToturn();
            // 所有商户配置请求失败，抛出错误
            return throwError(() => new Error('The merchant configuration request failed'));
          } else {
            // 尝试下一个
            return of(true);
          }
        },
      }),
      catchError(() => of(null)),
    );
  }

  /**域名测试并跳转 */
  testDomainToturn() {
    const urls: string[] | null = this.localStorageService.domainList;
    if (!urls) return;

    // 创建所有可用域名的检测队列
    const obs = urls.map(url => {
      const apiUrl = url + '/v1/connect' + '?t=' + Date.now();
      return this.http.get<string>(apiUrl, { headers: { 'ngsw-bypass': 'true' }, responseType: 'text' as 'json' }).pipe(
        switchMap(x => {
          if (x === 'ok') {
            return of(url);
          } else {
            return throwError(() => new Error('The returned content is invalid'));
          }
        }),
        timeout(10 * 1000), //10秒超时
        catchError(error => {
          this.sentryService.apiError(apiUrl, error, undefined, undefined, { domain: url });
          return of(null);
        }),
      );
    });

    // 返回第一个响应可用的，如果都不可用，返回null
    merge(...obs)
      .pipe(
        first(x => !!x),
        catchError(() => of(null)),
      )
      .subscribe(availableUrl => {
        // 逻辑说明:
        // -首个测试可用的域名会自动跳转
        // -没有可用的域名且不支持serviceWorker，什么都不做，停留在loading界面
        // -没有可用的域名且支持serviceWorker，清除缓存，5秒后原地刷新
        if (availableUrl) {
          window.location.href = availableUrl + window.location.search;
        } else if ('serviceWorker' in navigator) {
          window.navigator.serviceWorker.getRegistrations().then(registrations => {
            if (registrations.length > 0) {
              const obs = registrations.map(registration => from(registration.unregister()));
              forkJoin(obs).subscribe(() => {
                // 所有sw缓存清除完毕
                this.delayReload();
              });
            } else {
              // 无缓存，直接去刷新
              this.delayReload();
            }
          });
        }
      });
  }

  /**5秒后原地刷新 */
  delayReload() {
    setTimeout(() => {
      this.appService.reFresh();
    }, 5000);
  }

  /** native app 访问 初始化 */
  onCheckNativeApp() {
    const appParams = new URLSearchParams(location.search);
    this.appService.isNativeApp = appParams.get('isApp') === '1' ? true : false;
    if (this.appService.isNativeApp) {
      // 登录token
      const token = appParams.get('token');
      // 未登录token
      const jwtToken = appParams.get('jwtToken');
      document.body.classList.add('isNativeApp');
      this.localStorageService.theme = appParams.get('isDark') === '1' ? 'moon' : 'sun';
      this.appService.themeSwitch$.next(this.localStorageService.theme);
      this.nativeAppService.webAction();
      if (jwtToken) {
        this.localStorageService.loginToken = null;
        this.localStorageService.token = jwtToken;
      } else {
        this.localStorageService.token = this.localStorageService.loginToken = token;
      }
    }
  }

  /**执行优先级较低的东西 */
  afterReady() {
    // 設定 Mobile 瀏覽器網址列的主題色 (在删除全局等待的 DOM 後同步出現)
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    metaThemeColor?.setAttribute('content', '#00471e');

    // 订阅网络断网、页面不可见等
    combineLatest([
      merge(
        fromEvent(window, 'online').pipe(map(() => true)),
        fromEvent(window, 'offline').pipe(map(() => false)),
      ).pipe(startWith(navigator.onLine), distinctUntilChanged()),
      fromEvent(document, 'visibilitychange').pipe(
        map(e => (e.target as Document).visibilityState),
        startWith(document.visibilityState),
        map(v => v === 'visible'),
        distinctUntilChanged(),
      ),
    ])
      .pipe(untilDestroyed(this), startWith([null, null]), pairwise())
      .subscribe(([[PreOnline, PreVisibility], [online, visibility]]) => {
        if (!online && !this.appService.networkTips$.value) {
          // 显示断网提示
          this.appService.networkTips$.next(true);
        }
        if ((online && PreOnline === false) || (online && visibility && PreVisibility === false)) {
          if (this.localStorageService.loginToken) {
            // 重连操作
            this.appService.assetChanges$.next({ related: 'Wallet' });
          } else {
            this.appService.networkTips$.next(false);
          }
        }
      });

    this.route.queryParamMap.pipe(untilDestroyed(this)).subscribe(queryParam => {
      // 代理邀请码、aff邀请码，同一時間只會有個存在
      const inviteCode = queryParam.get('inviteCode');
      const aff = queryParam.get('aff');

      // URL 真的帶了新的 aff 或 inviteCode，才先清除原本已存儲的 aff 與 inviteCode
      const hasInviteCode = !!inviteCode && inviteCode.length > 0;
      const hasAff = !!aff && aff.length > 0;
      if (hasInviteCode || hasAff) {
        this.localStorageService.inviteCode = hasInviteCode ? inviteCode : null;
        this.localStorageService.myAffiliate = hasAff ? aff : null;
      }

      const modal = queryParam.get('modal');
      if (modal) {
        switch (modal) {
          case 'wheel':
            // this.activityService.openWheel();
            break;
          default:
            break;
        }
      }
    });

    if (this.localStorageService.defaultLang && this.localStorageService.loginToken) {
      this.settingsApi.setDefaultLang({ language: this.localStorageService.defaultLang }).subscribe(data => {
        if (data) {
          this.localStorageService.defaultLang = null;
        }
      });
    }

    // Show banner popup before login, but escape login page
    if (!this.localStorageService.loginToken && this.router.url.split('/')[2] !== 'login') {
      this.bannerPopupService.onShowPopupForBeforeLogin();
    }

    // 自动打开一次大转盘
    // if (!this.activityService.openWheeled && !this.localStorageService.wheelShown && !this.appService.isNativeApp) {
    //   this.activityService.checkToOpenWheel(5 * 1000);
    // }

    this.userAssetsService.allRate.pipe(untilDestroyed(this)).subscribe(data => {
      this.currencyWalletService.updateRate(data);
    });

    this.signalrService.init();

    //初始化一些serviceWorker事件
    this.swService.init();
  }

  /**
   * Telegram Mini App 自動登入
   *
   * 1. 沒有Token時，自動登入
   * 2. 有Token時，驗證Token有效性 (a. 有效: 不動作; b. 無效: 自動登入)
   */
  autoLoginTelegramMiniApp() {
    // Close the Mini App if in Telegram mini app
    const isTgApp = sessionStorage.getItem('isTgApp') === 'true';
    if (isTgApp) {
      this.thirdAuthM8Service.socialUserVerify({
        initData: window.Telegram?.WebApp?.initData,
        userType: 'Telegram',
      });

      window.Telegram?.WebApp?.ready();
    }
  }

  ngOnDestroy(): void {
    if (this.isReady) {
      this.dataCollectionService.addPoint({
        eventId: 30003,
        actionValue1: this.dataCollectionService.getTimDiff('allpage'),
      });
      // 提交上一次埋点信息
      this.dataCollectionService.destory();
    }
  }
}
